import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, FormEvent, ReactNode } from "react";
import { Button } from "./Button";
import { Container } from "./Container";
import { SectionHeading } from "./SectionHeading";
import { cn } from "../lib/cn";
import {
  CONTACT_ENDPOINT,
  CONTACT_LIMITS,
  CONTACT_TOPICS,
  COOLDOWN_MS,
  HONEYPOT_FIELD,
  validateContact,
} from "../lib/contact";
import type { ContactFields } from "../lib/contact";
import { siteConfig } from "../data/portfolio";

const EMPTY: ContactFields = {
  name: "",
  email: "",
  company: "",
  topic: CONTACT_TOPICS[0],
  message: "",
};

const FIELD_CLASSES =
  "border-hairline bg-faint text-strong placeholder:text-dim ease-standard w-full rounded-2xl border px-4 py-3 text-base leading-6 transition duration-200 hover:border-hairline-hover focus:border-hairline-hover disabled:opacity-60";

type Status = "idle" | "sending" | "sent" | "error";

/**
 * The contact form. Posts to the Worker at `/api/contact`, which relays the
 * message through Cloudflare Email Routing. Guarded by a honeypot, a minimum
 * fill time, and a cooldown that mirrors the Worker's per-IP rate limit.
 */
export function ContactSection() {
  const [form, setForm] = useState<ContactFields>(EMPTY);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");
  const [invalidField, setInvalidField] = useState<keyof ContactFields | null>(
    null,
  );
  const cooldownLeft = useCooldown(status === "sent");

  // Compared against the submit time so scripted instant submissions are caught.
  const mountedAt = useRef(Date.now());

  const set =
    (key: keyof ContactFields) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { value } = event.target;
      setForm((current) => ({ ...current, [key]: value }));
      if (invalidField === key) setInvalidField(null);
      if (status === "error") {
        setStatus("idle");
        setFeedback("");
      }
    };

  // Fields lock only while the request is in flight; the cooldown holds the
  // button alone, so the next message can be drafted meanwhile.
  const sending = status === "sending";
  const locked = sending || cooldownLeft > 0;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (locked) return;

    // The same check the Worker runs, so obvious mistakes never leave the page.
    const check = validateContact(form);
    if (!check.ok) {
      setInvalidField(check.field);
      setStatus("error");
      setFeedback(check.error);
      return;
    }

    setStatus("sending");
    setFeedback("");
    setInvalidField(null);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...check.value,
          elapsedMs: Date.now() - mountedAt.current,
          [HONEYPOT_FIELD]: honeypot,
        }),
      });

      const result = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        field?: keyof ContactFields;
      };

      if (!response.ok || !result.ok) {
        setStatus("error");
        setInvalidField(result.field ?? null);
        setFeedback(
          result.error ?? "Something went wrong. Please email me directly.",
        );
        return;
      }

      setForm(EMPTY);
      mountedAt.current = Date.now();
      setStatus("sent");
      setFeedback("Message sent — I'll reply by email.");
    } catch {
      setStatus("error");
      setFeedback(
        `Could not reach the server. Please email me at ${siteConfig.email}.`,
      );
    }
  }

  return (
    <section id="contact" className="py-16 md:py-24">
      <Container>
        <SectionHeading
          align="center"
          eyebrow="Get in touch"
          title="Have a role, project, or technical problem worth discussing?"
          description="Tell me what you're building and I'll get back to you by email."
        />

        <form
          onSubmit={submit}
          noValidate
          className="border-hairline bg-raised relative mx-auto mt-10 grid max-w-[44rem] gap-5 rounded-card border p-6 text-left shadow-sm sm:p-8"
        >
          <div className="grid gap-5 sm:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]">
            <Field label="Name" htmlFor="contact-name">
              <input
                id="contact-name"
                name="name"
                autoComplete="name"
                maxLength={CONTACT_LIMITS.name.max}
                value={form.name}
                onChange={set("name")}
                disabled={sending}
                placeholder="Your name"
                aria-invalid={invalidField === "name"}
                className={cn(
                  FIELD_CLASSES,
                  invalidField === "name" && "border-red-400/70",
                )}
              />
            </Field>

            <Field label="Email" htmlFor="contact-email">
              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                maxLength={CONTACT_LIMITS.email.max}
                value={form.email}
                onChange={set("email")}
                disabled={sending}
                placeholder="you@company.com"
                aria-invalid={invalidField === "email"}
                className={cn(
                  FIELD_CLASSES,
                  invalidField === "email" && "border-red-400/70",
                )}
              />
            </Field>

            <Field label="Company" hint="Optional" htmlFor="contact-company">
              <input
                id="contact-company"
                name="company"
                autoComplete="organization"
                maxLength={CONTACT_LIMITS.company.max}
                value={form.company}
                onChange={set("company")}
                disabled={sending}
                placeholder="Studio or team"
                className={FIELD_CLASSES}
              />
            </Field>

            <Field label="Topic" htmlFor="contact-topic">
              <div className="relative">
                <select
                  id="contact-topic"
                  name="topic"
                  value={form.topic}
                  onChange={set("topic")}
                  disabled={sending}
                  className={cn(FIELD_CLASSES, "appearance-none pr-11")}
                >
                  {CONTACT_TOPICS.map((topic) => (
                    <option key={topic} value={topic} className="bg-page">
                      {topic}
                    </option>
                  ))}
                </select>
                <Chevron />
              </div>
            </Field>
          </div>

          <Field
            label="Message"
            htmlFor="contact-message"
            hint={`${form.message.length} / ${CONTACT_LIMITS.message.max}`}
          >
            <textarea
              id="contact-message"
              name="message"
              rows={6}
              maxLength={CONTACT_LIMITS.message.max}
              value={form.message}
              onChange={set("message")}
              disabled={sending}
              placeholder="A few lines about the role, project, or problem."
              aria-invalid={invalidField === "message"}
              className={cn(
                FIELD_CLASSES,
                "resize-y",
                invalidField === "message" && "border-red-400/70",
              )}
            />
          </Field>

          <Honeypot value={honeypot} onChange={setHoneypot} />

          <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
            <Feedback status={status} message={feedback} />
            <Button
              type="submit"
              variant="accent"
              size="lg"
              icon="mail"
              iconPosition="left"
              disabled={locked}
            >
              {sending
                ? "Sending…"
                : cooldownLeft > 0
                  ? `Sent — wait ${cooldownLeft}s`
                  : "Send message"}
            </Button>
          </div>
        </form>
      </Container>
    </section>
  );
}

function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-2">
      <label
        htmlFor={htmlFor}
        className="flex items-baseline justify-between gap-3"
      >
        <span className="text-muted text-xs font-semibold tracking-eyebrow-sm uppercase">
          {label}
        </span>
        {hint ? (
          <span className="text-dim text-xs tabular-nums">{hint}</span>
        ) : null}
      </label>
      {children}
    </div>
  );
}

/**
 * Off-screen and hidden from assistive technology, so only a script that reads
 * the markup will fill it in. A filled value makes the Worker drop the message.
 */
function Honeypot({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
      <label htmlFor="contact-website">Leave this field empty</label>
      <input
        id="contact-website"
        name={HONEYPOT_FIELD}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function Feedback({ status, message }: { status: Status; message: string }) {
  if (!message) return <span />;

  const failed = status === "error";
  return (
    <p
      role="status"
      className={cn(
        "border-hairline bg-faint m-0 inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm",
        failed ? "text-red-300" : "text-body",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "size-2 shrink-0 rounded-full",
          failed ? "bg-red-400" : "bg-accent",
        )}
      />
      {message}
    </p>
  );
}

function Chevron() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-muted pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * Counts a send cooldown down to zero, one second at a time. Keeps the submit
 * button in step with the Worker's per-IP rate limit window.
 */
function useCooldown(start: boolean): number {
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!start) return;

    const until = Date.now() + COOLDOWN_MS;
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.ceil((until - Date.now()) / 1000)));

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [start]);

  return secondsLeft;
}
